/**
 * React Dependency Injection Context for Repositories
 * Allows components and hooks to consume injected repositories through React Context or container.
 */
import React, { createContext, useContext, ReactNode } from 'react';
import { repositoryContainer, RepositoryContainer } from './RepositoryContainer';
import { RepositoryToken } from './RepositoryTokens';

const RepositoryContext = createContext<RepositoryContainer>(repositoryContainer);

export interface RepositoryProviderProps {
  children: ReactNode;
  container?: RepositoryContainer;
}

export const RepositoryProvider: React.FC<RepositoryProviderProps> = ({
  children,
  container = repositoryContainer,
}) => {
  return (
    <RepositoryContext.Provider value={container}>
      {children}
    </RepositoryContext.Provider>
  );
};

/**
 * Hook to resolve any repository from React DI context
 */
export function useRepository<T>(token: RepositoryToken | string): T {
  const container = useContext(RepositoryContext);
  return container.get<T>(token);
}

/**
 * Hook to access the repository container itself
 */
export function useRepositoryContainer(): RepositoryContainer {
  return useContext(RepositoryContext);
}
